// Correio: pastas, lista, leitura, envio, marcadores.
//
// A regra que orienta tudo: a LISTA vem do índice no PostgreSQL, o CORPO vem
// do IMAP sob demanda. Falar IMAP a cada clique foi o gargalo que inviabilizou
// o Roundcube.

import { api } from "./cliente";
import type {
  Anexo,
  Cota,
  CursorDe,
  Mensagem,
  MensagemEnvio,
  MensagemResumo,
  Pasta,
  Resposta,
} from "./tipos";

export type FiltroLista = {
  pasta?: string;
  cursor?: string;
  limite?: number;
  nao_lidas?: boolean;
  marcadas?: boolean;
  com_anexo?: boolean;
}

// Pastas e cota vêm juntas: a barra lateral mostra as duas, e uma segunda
// chamada só para o medidor de espaço seria uma ida ao IMAP por carregamento.
export const pastas = (sinal?: AbortSignal) =>
  api.get<{ pastas: Pasta[]; cota: Cota }>("/correio/pastas", { sinal });

export const listar = (filtro: FiltroLista, sinal?: AbortSignal) =>
  api.get<CursorDe<MensagemResumo>>("/correio/mensagens", { consulta: filtro, sinal });

export const buscar = (termo: string, pasta?: string, sinal?: AbortSignal) =>
  api.get<CursorDe<MensagemResumo>>("/correio/mensagens/busca", {
    consulta: { q: termo, pasta },
    sinal,
  });

/**
 * Os marcadores EM USO na caixa, com a contagem de cada um.
 *
 * Não há tabela de marcadores: eles são keywords do IMAP, e existe o que
 * estiver em uso. Esta rota pergunta ao índice.
 *
 * Antes disto, a lista de sugestões do menu de marcadores vinha das
 * mensagens CARREGADAS na tela — abrindo uma mensagem direto, as sugestões
 * eram as daquela mensagem, quase sempre nenhuma. Era o apontamento 20.
 */
export type MarcadorEmUso = { nome: string; mensagens: number; nao_lidas: number };

export const marcadoresEmUso = (sinal?: AbortSignal) =>
  api.get<{ itens: MarcadorEmUso[] }>("/correio/marcadores", { sinal });

/**
 * `carregar_imagens` é falso por padrão, e é o servidor que decide.
 *
 * Imagem externa numa mensagem avisa a quem enviou que ela foi lida, de onde e
 * quando. O usuário libera por mensagem, conscientemente.
 */
export const ler = (uuid: string, opcoes?: { marcar_lida?: boolean; carregar_imagens?: boolean }) =>
  api.get<Mensagem>(`/correio/mensagens/${uuid}`, { consulta: opcoes });

/**
 * A conversa inteira a que a mensagem pertence, em TODAS as pastas.
 *
 * É o que permite ver a própria resposta ao abrir a mensagem recebida — ela
 * vive em "Enviados", e sem isto a tela mostra metade do diálogo.
 *
 * Só resumos: o CORPO de cada uma continua vindo do IMAP, uma por vez, ao
 * expandir. Trazer o corpo de uma conversa de trinta mensagens seria trinta
 * idas ao IMAP para mostrar uma.
 */
export const conversa = (uuid: string, sinal?: AbortSignal) =>
  api.get<{ thread_id: string; itens: MensagemResumo[] }>(
    `/correio/mensagens/${uuid}/conversa`,
    { sinal },
  );

export const marcar = (
  uuid: string,
  mudancas: { lida?: boolean; marcada?: boolean; marcadores?: string[] },
) => api.patch<MensagemResumo>(`/correio/mensagens/${uuid}`, mudancas);

export const apagar = (uuid: string) => api.delete<void>(`/correio/mensagens/${uuid}`);

export const mover = (mensagens: string[], pasta_destino: string) =>
  api.post<{ movidas: number; falhas: unknown[] }>("/correio/mensagens/mover", {
    mensagens,
    pasta_destino,
  });

export const enviar = (envio: MensagemEnvio) =>
  api.post<MensagemResumo>("/correio/mensagens", envio);

export const responder = (uuid: string, envio: MensagemEnvio) =>
  api.post<MensagemResumo>(`/correio/mensagens/${uuid}/responder`, envio);

export const encaminhar = (uuid: string, envio: MensagemEnvio) =>
  api.post<MensagemResumo>(`/correio/mensagens/${uuid}/encaminhar`, envio);

export const salvarRascunho = (envio: MensagemEnvio) =>
  api.post<MensagemResumo>("/correio/rascunhos", envio);

/**
 * O anexo vem sempre como application/octet-stream, mesmo declarando outro
 * tipo — servi-lo com o tipo que ele afirma ter deixaria HTML hostil executar
 * no domínio do webmail. Quem dá nome ao arquivo é a interface, não o
 * Content-Type.
 */
export const baixarAnexo = (uuid: string, parte: string) =>
  api.get<Blob>(`/correio/mensagens/${uuid}/anexos/${encodeURIComponent(parte)}`, {
    binario: true,
  });

export type { Anexo, Cota, Mensagem, MensagemResumo, Pasta };

/**
 * Ordena as pastas como o usuário espera vê-las.
 *
 * A ordem vem do IMAP, que não promete ordem nenhuma — hoje ele devolve
 * Archive antes da INBOX. E a identificação é pelo `uso_especial`
 * (SPECIAL-USE), nunca pelo nome: um servidor migrado pode ter "Elementos
 * enviados" ou "Itens Enviados" no lugar de "Sent", e comparar nome quebraria
 * em silêncio — a pasta apareceria, só no lugar errado da lista.
 *
 * Pasta criada pelo usuário não tem uso especial e vai para o fim, em ordem
 * alfabética.
 */
const ORDEM_ESPECIAL = ["\\Drafts", "\\Sent", "\\Archive", "\\Junk", "\\Trash"];

export function ordenar(pastas: Pasta[]): Pasta[] {
  const posicao = (p: Pasta): number => {
    if (p.nome === "INBOX") return 0;
    const i = ORDEM_ESPECIAL.indexOf(p.uso_especial ?? "");
    return i >= 0 ? i + 1 : ORDEM_ESPECIAL.length + 1;
  };
  return [...pastas].sort((a, b) => {
    const d = posicao(a) - posicao(b);
    if (d !== 0) return d;
    return (a.rotulo ?? a.nome ?? "").localeCompare(b.rotulo ?? b.nome ?? "", "pt-BR");
  });
}

// ---------------------------------------------------------------------------
// Gestão de pastas.
//
// A pasta é identificada pelo NOME, não por uuid: é assim que o IMAP a
// identifica, e inventar um uuid exigiria uma tabela para mapear o que o
// servidor de e-mail já sabe — e que muda quando o usuário renomeia a pasta
// por outro programa.
// ---------------------------------------------------------------------------

export const criarPasta = (nome: string) =>
  api.post<{ nome: string }>("/correio/pastas", { nome });

export const renomearPasta = (de: string, nome: string) =>
  api.patch<{ nome: string }>(`/correio/pastas/${encodeURIComponent(de)}`, { nome });

/** `confirmado` é obrigatório quando a pasta tem mensagens: apagá-la apaga o que está dentro. */
export const apagarPasta = (nome: string, confirmado = false) =>
  api.delete<void>(
    `/correio/pastas/${encodeURIComponent(nome)}` + (confirmado ? "?confirmado=true" : ""),
  );

/** Marcadores são keywords do IMAP: a mesma mensagem pode ter vários. */
export const marcadores = (uuid: string, lista: string[]) =>
  api.patch<MensagemResumo>(`/correio/mensagens/${uuid}`, { marcadores: lista });

/** O que a marcação em lote aceita mudar. */
export interface MudancaEmLote {
  lida?: boolean;
  marcada?: boolean;
  acrescentar_marcadores?: string[];
  tirar_marcadores?: string[];
}

export interface ResultadoEmLote {
  alteradas: number;
  falhas: { uuid: string; motivo: string }[];
}

/** Quantas mensagens cabem numa operação. O servidor recusa acima disso. */
export const MAXIMO_POR_LOTE = 500;

/**
 * Marca VÁRIAS mensagens numa requisição só.
 *
 * O caminho individual abre uma sessão IMAP por mensagem — cinquenta
 * selecionadas seriam cinquenta sessões para uma operação que a pessoa
 * percebe como única.
 */
export function marcarVarias(
  mensagens: string[],
  mudanca: MudancaEmLote,
): Promise<ResultadoEmLote> {
  return api.post<ResultadoEmLote>("/correio/mensagens/marcar", { mensagens, ...mudanca });
}

/** O arquivo que NÃO cabe como anexo: sobe e vira link no corpo da mensagem. */
export interface ArquivoSubido {
  uuid: string;
  nome: string;
  bytes: number;
  expira_em: string;
  url: string;
}

/**
 * Sobe em `multipart`, não em base64.
 *
 * Em base64 um arquivo de 180 MB viraria 240 MB de texto na memória — do
 * navegador E do servidor. Em multipart o navegador transmite em fluxo e o
 * servidor grava direto no disco.
 */
export const subirArquivo = (arquivo: File) => {
  const corpo = new FormData();
  corpo.append("arquivo", arquivo);
  return api.post<ArquivoSubido>("/correio/arquivos", corpo);
};

/**
 * O QUE JÁ ESTÁ GUARDADO, e é a metade que faltava.
 *
 * `subirArquivo` existia só; listar e revogar não tinham cliente nenhum. O
 * efeito era que subir um arquivo criava um endereço PÚBLICO de 30 dias, e a
 * pessoa não tinha como ver o que havia criado nem matar um link mandado por
 * engano — a API fazia as duas coisas desde o primeiro dia.
 *
 * O `estado` vem CALCULADO do servidor (`ativo`, `vencido`, `revogado`,
 * `apagado`). Não recalcular aqui é de propósito: três telas decidindo
 * "vencido" por conta própria divergem.
 */
export const arquivosGuardados = () =>
  api.get<Resposta<"/correio/arquivos">>("/correio/arquivos");

/** Mata o link AGORA. O arquivo sai do disco na próxima varredura. */
export const revogarArquivo = (uuid: string) =>
  api.delete<void>(`/correio/arquivos/${uuid}`);

// ---------------------------------------------------------------------------
// PROGRAMAR ENVIO
// ---------------------------------------------------------------------------

export type Agendado = Resposta<"/correio/agendados">[number];

/**
 * Programa uma mensagem para sair mais tarde.
 *
 * `quando` vai em ISO COM FUSO — `toISOString()` serve, porque ele emite em
 * UTC com o `Z`, que é fuso explícito. O que NÃO serve é mandar o horário
 * local sem fuso: "amanhã às 8" viraria 8h UTC e a mensagem sairia às 5 da
 * manhã no Brasil.
 */
export const agendarEnvio = (envio: MensagemEnvio, quando: Date, modo?: string, origem?: string) =>
  api.post<Resposta<"/correio/agendados", "post">>("/correio/agendados", {
    ...envio,
    quando: quando.toISOString(),
    ...(modo && modo !== "enviar" ? { modo, origem } : {}),
  });

export const agendados = () => api.get<Resposta<"/correio/agendados">>("/correio/agendados");

/**
 * Cancela, e DEVOLVE a mensagem.
 *
 * Antes isto respondia 204 e o texto ficava inalcançável — cancelar um envio
 * programado perdia o que a pessoa havia escrito. Agora o corpo volta, e é
 * o que torna "editar um envio programado" possível: cancelar e reabrir.
 *
 * Os anexos NÃO voltam, só os nomes: ver o contrato.
 */
export type AgendadoCancelado = {
  para?: string[];
  cc?: string[];
  assunto?: string;
  corpo?: string;
  anexos_perdidos?: string[];
};

export const cancelarAgendado = (uuid: string) =>
  api.delete<AgendadoCancelado>(`/correio/agendados/${encodeURIComponent(uuid)}`);
